import {
  Box,
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  InputText,
  Radio,
  Text,
  Textarea,
} from '@dixa/lib-shared-habla';
import { getFieldMeta } from 'lib-knowledge-form';
import { SimilarityLevel } from 'lib-knowledge-management-common/graphql';
import { Control, Controller } from 'react-hook-form';

import { useShowInternalOnlyCollections } from 'hooks/useShowInternalOnlyCollections';

import {
  AiSettingsFormValues,
  MAX_INSTRUCTIONS_LENGTH,
  MAX_SIMILAR_CONVERSATIONS,
  MIN_SIMILAR_CONVERSATIONS,
} from '../utils/aiSettingsForm';

import styles from './aiSettingsFields.module.scss';

type FieldsProps = {
  control: Control<AiSettingsFormValues>;
  disabled: boolean;
};

const similarityOptions: {
  value: SimilarityLevel;
  label: string;
  description: string;
}[] = [
  {
    value: SimilarityLevel.Broad,
    label: 'Broad',
    description:
      'Groups loosely related conversations. You get more suggestions, but some may mix different issues.',
  },
  {
    value: SimilarityLevel.Default,
    label: 'Default',
    description:
      'Balances finding gaps against avoiding noise. Recommended for most organizations.',
  },
  {
    value: SimilarityLevel.Strict,
    label: 'Strict',
    description:
      'Only groups conversations that are very alike. You get fewer suggestions, focused on clear gaps.',
  },
];

function SimilarityField({ control, disabled }: FieldsProps) {
  return (
    <Controller
      control={control}
      name="similarity"
      render={({ field, fieldState }) => (
        <FieldWrapper
          label="How similar conversations must be"
          helpIcon={{
            text: 'Conversations count as the same issue when the customer had the same problem, and it was handled the same way.',
          }}
          meta={getFieldMeta(fieldState)}
        >
          <FlexBox flexDirection="column" gap="200" role="radiogroup">
            {similarityOptions.map((option) => {
              const id = `similarity-${option.value}`;
              return (
                <div key={option.value}>
                  <FlexBox gap="100" alignItems="center">
                    <Radio
                      id={id}
                      name={field.name}
                      value={option.value}
                      checked={field.value === option.value}
                      onChange={() => field.onChange(option.value)}
                      onBlur={field.onBlur}
                      disabled={disabled}
                    />
                    <label htmlFor={id} className={styles.radioLabel}>
                      {option.label}
                    </label>
                  </FlexBox>
                  <Box margin="0 0 0 500">
                    <Text.Paragraph variant="4" subdued>
                      {option.description}
                    </Text.Paragraph>
                  </Box>
                </div>
              );
            })}
          </FlexBox>
        </FieldWrapper>
      )}
    />
  );
}

function MinSimilarConversationsField({ control, disabled }: FieldsProps) {
  return (
    <Controller
      control={control}
      name="minSimilarConversations"
      render={({ field, fieldState }) => (
        <FieldWrapper
          label="Minimum similar conversations"
          labelFor={field.name}
          description={`How many conversations about the same issue it takes before the AI checks for a gap, the one that just closed included. From ${MIN_SIMILAR_CONVERSATIONS} to ${MAX_SIMILAR_CONVERSATIONS}.`}
          meta={getFieldMeta(fieldState)}
        >
          <InputText
            className={styles.numberInput}
            type="number"
            name={field.name}
            min={MIN_SIMILAR_CONVERSATIONS}
            max={MAX_SIMILAR_CONVERSATIONS}
            step={1}
            value={Number.isNaN(field.value) ? '' : field.value}
            // Empty while being retyped; validation says what is missing.
            onChange={({ target }) =>
              field.onChange(target.value === '' ? NaN : Number(target.value))
            }
            onBlur={field.onBlur}
            disabled={disabled}
          />
        </FieldWrapper>
      )}
    />
  );
}

function InstructionsField({
  control,
  disabled,
  name,
  label,
  description,
  placeholder,
}: FieldsProps & {
  name:
    | 'judgeInstructions'
    | 'internalJudgeInstructions'
    | 'writerInstructions';
  label: string;
  description: string;
  placeholder: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { ref, ...fieldWithoutRef } = field;
        return (
          <FieldWrapper
            label={label}
            labelFor={name}
            description={description}
            meta={getFieldMeta(fieldState)}
          >
            <Textarea
              {...fieldWithoutRef}
              id={name}
              // Textarea drops `className`, so its size goes inline.
              style={{ minHeight: 120, resize: 'vertical' }}
              placeholder={placeholder}
              disabled={disabled}
            />
            <Text.Paragraph
              variant="4"
              subdued
              className={styles.characterCount}
            >
              {field.value.trim().length.toLocaleString('en')} /{' '}
              {MAX_INSTRUCTIONS_LENGTH.toLocaleString('en')}
            </Text.Paragraph>
          </FieldWrapper>
        );
      }}
    />
  );
}

/** What decides when a conversation points at a gap worth an article. */
export function GapDetectionFields(props: FieldsProps): JSX.Element {
  const showInternalOnlyCollections = useShowInternalOnlyCollections();

  return (
    <FormGrid>
      <FormField>
        <SimilarityField {...props} />
      </FormField>
      <FormField>
        <MinSimilarConversationsField {...props} />
      </FormField>
      <FormField>
        <InstructionsField
          {...props}
          name="judgeInstructions"
          label="Instructions for finding gaps"
          description="Tell the AI what to look out for when it decides whether conversations point at missing or outdated knowledge. For example, topics to always flag or to ignore."
          placeholder="Ignore questions about order status. Customers can track their orders in their account."
        />
      </FormField>
      {showInternalOnlyCollections && (
        <FormField>
          <InstructionsField
            {...props}
            name="internalJudgeInstructions"
            label="Instructions for finding gaps in internal only collections"
            description="Tell the AI what to look out for when it decides whether conversations point at missing or outdated knowledge for your agents. For example, internal procedures to always flag or to ignore."
            placeholder="Ignore questions about shift schedules. Agents can find them in the staff portal."
          />
        </FormField>
      )}
    </FormGrid>
  );
}

/** What shapes the drafts the AI writes and revises. */
export function DraftWritingFields(props: FieldsProps): JSX.Element {
  return (
    <FormGrid>
      <FormField>
        <InstructionsField
          {...props}
          name="writerInstructions"
          label="Instructions for writing drafts"
          description="Tell the AI how to write and revise AI drafts. For example, the tone of voice, the structure, or terms to use or avoid."
          placeholder="Write in a friendly, informal tone. Start with a one-sentence summary, then list the steps."
        />
      </FormField>
    </FormGrid>
  );
}
